import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Camera, History, ShieldCheck, Inbox, AlertCircle } from "lucide-react";
import { LISTING_STATUS, type ListingStatus } from "@avestate/shared";
import { useListing, usePatchListing, usePublishListing, useDiscardChanges } from "@/hooks/useListing.js";
import { Button } from "@/components/ui/button.js";
import { Badge } from "@/components/ui/badge.js";
import { StatusPill } from "@/components/signature/StatusPill.js";
import { RoleGate } from "@/components/RoleGate.js";
import { Permalink } from "@/components/Permalink.js";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";
import { listingUrl } from "@/lib/site.js";
import { cn } from "@/lib/cn.js";

const FIELD_LABEL: Record<string, string> = {
  title: "Titolo", description: "Descrizione", prezzo: "Prezzo", mq: "Mq",
  ape: "Classe energetica (APE)", city: "Città", property_type: "Tipologia",
};

/**
 * Persistent per-listing bar shown at the top of every listing sub-page
 * (editor, media, snapshots, approvals). Owns status, publish, save-as-draft
 * and discard-changes so the publishing controls are the same everywhere.
 */
export function ListingChrome({ id }: { id: string }): JSX.Element | null {
  const { data: listing } = useListing(id);
  const patch = usePatchListing(id);
  const publish = usePublishListing(id);
  const discard = useDiscardChanges(id);
  const loc = useLocation();
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishMissing, setPublishMissing] = useState<string[]>([]);
  const [discardOpen, setDiscardOpen] = useState(false);

  if (!listing) return null;
  const status = listing.status;
  const dirty = listing.has_unpublished_changes ?? false;

  const tabs = [
    { to: `/listings/${id}`, label: "Editor", Icon: Sparkles, exact: true },
    { to: `/listings/${id}/media`, label: "Media", Icon: Camera, exact: false },
    { to: `/listings/${id}/snapshots`, label: "Snapshot", Icon: History, exact: false },
    { to: `/listings/${id}/approvals`, label: "Approvazioni", Icon: ShieldCheck, exact: false },
    { to: `/inbox?listing_id=${id}`, label: "Lead", Icon: Inbox, exact: false },
  ];
  const isActive = (t: { to: string; exact: boolean }) =>
    t.exact ? loc.pathname === t.to : loc.pathname.startsWith(t.to.split("?")[0]!);

  async function onPublish() {
    try {
      const r = await publish.mutateAsync();
      toast.success(`Pubblicato · snapshot ${r.snapshot_id.slice(0, 8)}… · cache purgata`);
    } catch (e) {
      const err = e as { status?: number; details?: { missing?: string[] }; message: string };
      if (err.status === 409) {
        setPublishMissing(err.details?.missing ?? []);
        setPublishOpen(true);
        return;
      }
      toast.error(err.message);
    }
  }

  async function onSaveDraft() {
    try {
      await patch.mutateAsync({ status: "bozza" });
      toast.success("Salvato come bozza");
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function onDiscard() {
    try {
      await discard.mutateAsync();
      setDiscardOpen(false);
      toast.success("Modifiche scartate · tornato alla versione pubblicata");
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <div className="sticky top-0 z-20 -mx-4 mb-4 border-b border-rule bg-surface/95 px-4 pb-0 pt-3 backdrop-blur sm:-mx-6 sm:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-meta uppercase tracking-wider text-ink-mute">
            {listing.kind} · <Link to="/listings" className="hover:text-ink">immobili</Link>
          </p>
          <div className="mt-0.5 flex items-center gap-2">
            <StatusPill status={status} />
            {dirty ? (
              <Badge tone="accent" className="gap-1">
                <AlertCircle className="size-3" /> modifiche non pubblicate
              </Badge>
            ) : (
              <span className="font-mono text-meta uppercase tracking-wider text-ink-mute">
                {listing.published_snapshot_id ? "pubblicato · aggiornato" : "mai pubblicato"}
              </span>
            )}
          </div>
          <div className="mt-1"><Permalink url={listingUrl(listing.slug)} /></div>
        </div>

        <RoleGate min="editor">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={status}
              onChange={(e) => void patch.mutateAsync({ status: e.target.value as ListingStatus })}
              className="h-9 rounded-sm border border-rule bg-surface-2 px-3 text-body text-ink"
              aria-label="Stato pubblicazione"
            >
              {LISTING_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {status !== "bozza" ? (
              <Button size="sm" variant="ghost" onClick={() => void onSaveDraft()} disabled={patch.isPending}>
                Salva bozza
              </Button>
            ) : null}
            {dirty ? (
              <Button size="sm" variant="ghost" onClick={() => setDiscardOpen(true)} disabled={discard.isPending}>
                Scarta modifiche
              </Button>
            ) : null}
            <RoleGate min="admin" fallback={null}>
              {/* Media, posizione e stato sono LIVE sul sito (overlay nel worker):
                  pubblicare serve solo per il CONTENUTO, quindi si abilita solo
                  quando ci sono modifiche di contenuto non pubblicate. */}
              <Button size="sm" variant="primary" onClick={() => void onPublish()} disabled={publish.isPending || !dirty}>
                {publish.isPending ? "Pubblico…" : "Pubblica"}
              </Button>
            </RoleGate>
          </div>
        </RoleGate>
      </div>

      <nav className="mt-3 flex gap-1" aria-label="Sezioni immobile">
        {tabs.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            className={cn(
              "flex items-center gap-2 border-b-2 px-3 py-2 font-mono text-meta uppercase tracking-wider transition-colors",
              isActive(t) ? "border-accent text-ink" : "border-transparent text-ink-mute hover:text-ink",
            )}
          >
            <t.Icon className="size-3.5" /> {t.label}
          </Link>
        ))}
      </nav>

      <ConfirmDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        title="Impossibile pubblicare"
        description={
          <div className="space-y-2">
            <p>Mancano campi richiesti per la pubblicazione:</p>
            <ul className="ml-4 list-disc font-mono text-meta uppercase tracking-wider text-risk">
              {publishMissing.map((k) => <li key={k}>{FIELD_LABEL[k] ?? k}</li>)}
            </ul>
            <p className="text-meta text-ink-mute">Compila i campi mancanti e riprova.</p>
          </div>
        }
        confirmLabel="Ho capito"
        cancelLabel="Chiudi"
        onConfirm={() => setPublishOpen(false)}
      />

      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Scartare le modifiche?"
        description="Le modifiche di contenuto non pubblicate verranno eliminate e la bozza tornerà all'ultima versione pubblicata. Le foto non sono interessate (sono già online in tempo reale)."
        confirmLabel="Scarta modifiche"
        cancelLabel="Annulla"
        onConfirm={() => void onDiscard()}
      />
    </div>
  );
}
